import { Component, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { NgxSpinnerService } from "ngx-spinner";
import { distinctUntilChanged, interval, merge, startWith, Subscription, tap } from "rxjs";
import {
  JobLimits,
  UserAppDetails,
  UserDetails,
  UserProfileResponse,
  UserRoleNames,
  Users,
} from "src/app/core/models/users";
import { DeploymentService } from "src/app/core/services/deployment.service";
import { UserService } from "src/app/core/services/user.service";
import { JobPriorityText } from "../../../../core/models/job";
import { AuthenticationService } from "../../../../core/services/authentication.service";
import { HeaderService } from "../../../../core/services/header.service";
import { AttachDeploymentComponent } from "./dialogs/attach-deployment/attach-deployment.component";
import { DetachDeploymentComponent } from "./dialogs/detach-deployment/detach-deployment.component";
import { UpdateJobSubmitIdComponent } from "./dialogs/update-job-submit-id/update-job-submit-id.component";
import { SyncConfigService } from "src/app/core/services/sync-config.service";

@Component({
  selector: "app-user-access-control",
  templateUrl: "./user-access-control.component.html",
  styleUrls: ["./user-access-control.component.scss"],
  host: {
    class: "h-100",
  },
})
export class UserAccessControlComponent implements OnInit {
  toShowAttachDeployment: boolean = true;
  userDetails: Users;
  selectedUserName: string;
  getUserDetailsSub: Subscription;
  userInfoesData: UserDetails[] = [];
  userInfoes: UserDetails[];
  showDataEmpty: boolean = false;
  deploymentNames: string[] = [];
  loggedInUser: string = this.authenticationService.currentUserValue.username;
  currentUserRole: string = this.authenticationService.getRole();
  showAttachDeployment: boolean = false;
  isMonitor: boolean =
    this.authenticationService.currentUserValue.role == UserRoleNames.Reader
      ? true
      : false;
  showDetachButton: boolean = true;
  isLoading: boolean = false;

  constructor(
    private dialog: MatDialog,
    private userService: UserService,
    private depService: DeploymentService,
    private authenticationService: AuthenticationService,
    private snackBar: MatSnackBar,
    private spinner: NgxSpinnerService,
    private _headerService: HeaderService,
    private _syncConfigService: SyncConfigService,
  ) { }

  ngOnInit(): void {
    this.deploymentNames = this._headerService.getQuickAccessDeploymentNames();
    this.getUserDetailsSub = merge(
      this.userService.userName.pipe(
        distinctUntilChanged(),
        tap((name) => {
          this.selectedUserName = name;
          this.loadUserInfoes(); // run once on change
        })
      ),
      interval(this._syncConfigService.config?.users?.userAccessReqInterval || 10000).pipe(
        startWith(0),
        tap(() => this.loadUserInfoes(true)) // keep refreshing
      )
    ).subscribe();
  }

  ngOnDestroy() {
    this.getUserDetailsSub?.unsubscribe();
  }


  loadUserInfoes(fromInterval = false) {
    if (fromInterval == false) {
      this.isLoading = true;
      this.spinner.show('userAccessControlSpinner');
    }
    // this.userInfoesData = [];
    this.userService
      .getUserProfileInfoes(this.selectedUserName)
      .subscribe({
        next: (data) => {
          if (data != null) {
            this.userInfoesData = [];
            this.userInfoes = <UserDetails[]>JSON.parse(JSON.stringify(data));
            this.updateLicenseNames();
            this.userInfoes.forEach((x) => {
              if (this.deploymentNames.includes(x.deploymentName)) {
                this.userInfoesData.push(x);
              }
            });
            if (
              Number(this.userInfoesData[0].userRole) == 0 ||
              Number(this.userInfoesData[0].userRole) == 4
            ) {
              this.showAttachDeployment = false;
            } else {
              this.showAttachDeployment = true;
            }
            this.userInfoesData.forEach(async (info, index) => {
              info.toShowActions = await this.showActions(
                Number(info.userRole),
                info.deploymentName
              );
            });
            this.showDataEmpty = false;
            this.userInfoesData.sort((a, b) => Number(a.isGroupAttached) - Number(b.isGroupAttached));
          } else {
            this.showDataEmpty = true;
            this.showAttachDeployment = true;
          }
          this.isLoading = false;
          this.spinner.hide('userAccessControlSpinner');
        },
        error: (err) => {
          this.isLoading = false;
          this.spinner.hide('userAccessControlSpinner');
          // Handle error
        }
      });
  }

  attachDeployment() {
    if (
      this.currentUserRole != UserRoleNames.GlobalAdmin &&
      this.currentUserRole != UserRoleNames.DeploymentAdmin
    ) {
      return;
    }
    if (this.toShowAttachDeployment) {
      let nonAttachedDeployments = this.deploymentNames;
      if (this.userInfoesData) {
        nonAttachedDeployments = this.deploymentNames.filter(
          (x) => !this.userInfoesData?.map((y) => y.deploymentName).includes(x)
        );
      }

      const dialogRef = this.dialog.open(AttachDeploymentComponent, {
        autoFocus: false,
        disableClose: false,
        width: "25%",
        data: {
          userName: this.selectedUserName,
          adminId: this.loggedInUser,
          deploymentNames: nonAttachedDeployments,
          isGroupsAccessControl: false,
        },
      });
      dialogRef.afterClosed().subscribe((data) => {
        if (data) {
          this.spinner.show();
          this.userService.attachDeployment(data).subscribe({
            next: (data: any) => {
              this.snackBar.open(data.message, "Ok", { duration: 5000 });
              this.loadUserInfoes();
              this.spinner.hide();
            },
            error: (err: any) => {
              this.snackBar.open(
                err.error.message || "Action cannot be completed.",
                "Ok",
                { duration: 5000 }
              );
              this.loadUserInfoes();
              this.spinner.hide();
            },
          });
        }
      });
    } else {
      this.snackBar.open("Cannot Attach Global Admin", "Ok", {
        duration: 5000,
      });
    }
  }

  async updateUserRole(
    userRole: number,
    profileId: string,
    nodeGroup: string,
    jobPriority: string,
    jobSubmitId: string,
    profileCoreQuota: number,
    mapWorkstation: string,
    userApps: UserAppDetails,
    sessionUserId: string,
    isVdi: boolean = false,
    assignedStorages: string
  ) {
    // if (this.showAttachDeployment) return;
    let performAction = await this.showActions(userRole, profileId);
    if (!performAction) {
      this.snackBar.open("Action cannot be performed.", "Ok", {
        duration: 5000,
      });
      return;
    }
    const userDetails = this.userInfoesData.find(
      (u) => u.deploymentName === profileId
    );
    if (userRole == 0 || userRole == 4) {
      const dialogRef = this.dialog.open(UpdateJobSubmitIdComponent, {
        autoFocus: false,
        disableClose: false,
        width: "50%",
        data: {
          userInfo: {
            userName: this.selectedUserName,
            adminId: this.loggedInUser,
            currentUserRole: userRole,
            mapWorkstation: mapWorkstation,
            deploymentName: profileId,
            forGlobalAdminOrReader: true,
            title: "Update access control",
            jobPriority: JobPriorityText.Highest,
            nodeGroup: "Default",
            jobSubmitId: this.selectedUserName,
            allocatedCores: -1,
            sessionUserId: sessionUserId,
            isVdi: isVdi,
          },
          JobsLimits: {
            maxJobs: userDetails.maxJobs,
            maxSubmitJobs: userDetails.maxSubmitJobs,
          },
        },
      });
      dialogRef.afterClosed().subscribe((data) => {
        if (data) {
          this.userService.updateJobSubmitUser(data).subscribe({
            next: (data: any) => {
              this.snackBar.open(data.message, "Ok", { duration: 5000 });
              this.loadUserInfoes();
            },
            error: (err: any) => {
              if (err.status === 409) {
                // 409 is partial success - show message and refresh data
                this.snackBar.open(err.error.message, "Ok", { duration: 5000 });
                this.loadUserInfoes(); // Refresh data because profile was updated
              } else {
                let errorMessage = err.error?.message || "Action cannot be completed.";
                this.snackBar.open(errorMessage, "Ok", { duration: 5000 });
              }
            },
          });
        }
      });
    } else {
      const dialogRef = this.dialog.open(UpdateJobSubmitIdComponent, {
        autoFocus: false,
        disableClose: true,
        width: "50%",
        data: {
          userInfo: {
            userName: this.selectedUserName,
            currentUserRole: userRole,
            deploymentName: profileId,
            adminId: this.loggedInUser,
            nodeGroup: nodeGroup,
            mapWorkstation: mapWorkstation,
            jobPriority: jobPriority,
            jobSubmitId: jobSubmitId ? jobSubmitId : this.selectedUserName,
            allocatedCores: profileCoreQuota,
            isGroupsAccessControl: false,
            userApps: userApps,
            fromUserGroup: false,
            title: "Update User Access Control",
            sessionUserId: sessionUserId,
            isVdi: isVdi,
            assignedStorages: assignedStorages,
          },
          JobsLimits: {
            maxJobs: userDetails.maxJobs,
            maxSubmitJobs: userDetails.maxSubmitJobs,
          },
        },
      });
      dialogRef.afterClosed().subscribe((data) => {
        if (data) {
          this.userService.updateJobSubmitUser(data).subscribe({
            next: (data: any) => {
              this.snackBar.open(data.message, "Ok", { duration: 5000 });
              this.loadUserInfoes();
            },
            error: (err: any) => {
              if (err.status === 409) {
                // 409 is partial success - show message and refresh data
                this.snackBar.open(err.error.message, "Ok", { duration: 5000 });
                this.loadUserInfoes(); // Refresh data because profile was updated
              } else {
                let errorMessage = err.error?.message || "Action cannot be completed.";
                this.snackBar.open(errorMessage, "Ok", { duration: 5000 });
              }
            },
          });
        }
      });
    }
  }

  async detachDeployment(userRole: number, profileId: string, isVdi: boolean = false) {
    // if (this.showAttachDeployment) return;
    let performAction = await this.showActions(userRole, profileId);
    if (!performAction) {
      this.snackBar.open("Action cannot be performed.", "Ok", {
        duration: 5000,
      });
      return;
    }
    const dialogRef = this.dialog.open(DetachDeploymentComponent, {
      autoFocus: false,
      disableClose: true,
      width: "25%",
      data: {
        userName: this.selectedUserName,
        adminId: this.loggedInUser,
        deploymentName: profileId,
        isUserDetach: true,
        isVdi: isVdi
      },
    });
    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.spinner.show();
        this.userService.detachDeployment(data).subscribe({
          next: (data: any) => {
            this.snackBar.open(data.message, "Ok", { duration: 5000 });
            this.spinner.hide();
            this.loadUserInfoes();
          },
          error: (err: any) => {
            this.snackBar.open(
              err.error.message || "Action cannot be completed.",
              "Ok",
              { duration: 5000 }
            );
            this.spinner.hide();
          },
        });
      }
    });
  }

  async showActions(userRole: number, depName: string) {
    let currentUserRole = this.authenticationService.currentUserValue.role;
    let role = await this.authenticationService.checkUserDeploymentAccess(
      depName
    );
    switch (userRole) {
      case 0:
        this.showDetachButton = false;
        if (currentUserRole == UserRoleNames.GlobalAdmin) {
          return true;
        }
        return false;
      case 1:
        this.showDetachButton = true;
        if (currentUserRole == UserRoleNames.GlobalAdmin) {
          return true;
        }
        if (
          (role == 1 && this.loggedInUser == this.selectedUserName) ||
          role == 0
        ) {
          return true;
        }
        return false;
      case 3:
        this.showDetachButton = true;
        if (role == 1 || role == 0) {
          return true;
        }
        return false;
      case 4:
        this.showDetachButton = false;
        if (currentUserRole == UserRoleNames.GlobalAdmin) {
          return true;
        }
        return false;
      default:
        return false;
    }
  }

  async updateUserProfileForGlobalAdminAndReader(
    userRole: number,
    profileId: string,
    mapWorkstation: string,
    sessionUserId: string
  ) {
    const dialogRef = this.dialog.open(UpdateJobSubmitIdComponent, {
      autoFocus: false,
      disableClose: false,
      width: "25%",
      data: {
        userInfo: {
          userName: this.selectedUserName,
          adminId: this.loggedInUser,
          currentUserRole: userRole,
          mapWorkstation: mapWorkstation,
          deploymentName: profileId,
          forGlobalAdminOrReader: true,
          title: "Update User Access Control",
          jobPriority: JobPriorityText.Highest,
          nodeGroup: "Default",
          jobSubmitId: this.selectedUserName,
          allocatedCores: -1,
          sessionUserId: sessionUserId,
        },
        JobsLimits: {
          maxJobs: null,
          maxSubmitJobs: null,
        },
      },
    });
    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.userService.updateJobSubmitUser(data).subscribe({
          next: (data: any) => {
            this.snackBar.open(data.message, "Ok", { duration: 5000 });
            this.loadUserInfoes();
          },
          error: (err: any) => {
            if (err.status === 409) {
              // 409 is partial success - show message and refresh data
              this.snackBar.open(err.error.message, "Ok", { duration: 5000 });
              this.loadUserInfoes(); // Refresh data because profile was updated
            } else {
              let errorMessage = err.error?.message || "Action cannot be completed.";
              this.snackBar.open(errorMessage, "Ok", { duration: 5000 });
            }
          },
        });
      }
    });
  }

  updateLicenseNames() {
    this.userInfoesData.forEach((x) => {
      x.userAppDetails.deploymentAppList.forEach((y) => {
        y.avlLicenses = y.avlLicenses.map((z) => y.appName + ":" + z);
      });
    });
  }

  trackByDeploymentFunction(index: number, item: UserDetails): string {
    return item.deploymentName;
  }
}
